import { useEffect, useMemo, useState } from "react";
import { useMatch, useLocation, useNavigate } from "react-router-dom";

// formik components
import { Formik, Form } from "formik";

// Auth0
import { useAuth0 } from "@auth0/auth0-react";

// PropTypes
import PropTypes from "prop-types";

// Global controller
import { useSoftUIController } from "hooks/context";

// @mui material components
import { Card, Stepper, Step, StepLabel, CircularProgress } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";

import Swal from "sweetalert2";
import withReactContent from 'sweetalert2-react-content';

// NewUser layout schemas for form and form feilds
import form from "./schemas/form";
import validationSchema from "./schemas/validationSchema";
import defaultServiceValues from "./schemas/initialValues";

import DeviceConditions from "./wizards/DeviceConditions";
import TechnicianActions from "./wizards/TechnicianActions";
import AdditionalNotes from "./wizards/AdditionalNotes";
import Review from "./wizards/Review";

import imageCompression from 'browser-image-compression';
import async from 'async';
import SoftTypography from "components/SoftComponents/SoftTypography";
import { updateProgressUI } from "modules/inspections/utils/submission";

import bksAPI from "services/api/bks";
import reportAPI from "services/api/reports";

import { useMutation, useQuery } from "@tanstack/react-query";
import { processDeviceData } from "utils/device";
import moment from "moment";
import { determineInspectionStatus } from "utils/inspection";
import { renderContent } from "modules/inspections/utils/submission";

function getStepContent(stepIndex, formData, deviceLayout, userPermissions) {
    switch (stepIndex) {
        case 0:
            return <DeviceConditions {...{ formData, deviceLayout, userPermissions }} />;
        case 1:
            return <TechnicianActions {...{ formData, deviceLayout, userPermissions }} />;
        case 2:
            return <AdditionalNotes {...{ formData, deviceLayout, userPermissions }} />;
        // case 2:
        //     return <Review formData={formData} />;
        default:
            return null;
    }
}

function AssignmentForm({ selected_user, all_devices, maintenance_tags, inspection_record, onSubmit }) {
    const navigate = useNavigate();
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    bksAPI.loadToken(useAuth0());
    reportAPI.loadToken(useAuth0());

    const [deviceOptions, setDeviceOptions] = useState([]);
    const [fetchedUuids, setFetchedUuids] = useState(typeof inspection_record?.devices?.[0]?.uuid != 'undefined' ? [inspection_record.devices[0].uuid] : []);
    const [maintenanceOptions] = useState(maintenance_tags.filter(t => t.status != 'complete').map(m => { return { label: m.tag_label, value: m.tag_label } }));

    const [activeStep, setActiveStep] = useState(0);
    const steps = ["Conditions", "Maintenance/Repairs", "Notes & Images"];
    const isLastStep = activeStep === steps.length - 1;

    const { formId, formField } = form;
    const currentValidation = validationSchema[activeStep] || Yup.object().shape({});

    const { data: fetchedData } = useQuery({
        queryKey: ['getDevices', fetchedUuids.join(','), selectedOrg],
        enabled: fetchedUuids.length > 0,
        queryFn: async () => await bksAPI.getDevices(fetchedUuids),
        refetchInterval: 5 * 1000
    });

    const fetchDevices = {};
    if (typeof fetchedData != 'undefined') {
        for (var d of fetchedData) {
            if (typeof fetchDevices[d.uuid] == 'undefined') fetchDevices[d.uuid] = processDeviceData(moment().unix() * 1000, d);
        }
    }

    useEffect(() => {
        const recordUuid = inspection_record?.devices?.[0]?.uuid;
        if (recordUuid && !fetchedUuids.includes(recordUuid)) {
            setFetchedUuids([recordUuid]);
        }
    }, [inspection_record]);

    const handleSubmit = (values, actions) => {
        // console.log(values, actions);
        if (isLastStep) {
            submitForm(values, actions);
        } else {
            setActiveStep(activeStep + 1);
            actions.setTouched({});
            actions.setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleBack = () => setActiveStep(activeStep - 1);

    function getObjValues(values, label) {
        var ret = {};
        var label_values = Object.keys(values).filter((row) => row.includes(label));

        for (var el of label_values) {
            var element_arr = el.split('-');
            var type = element_arr[(element_arr.length - 1)];
            ret[type] = values[`${label}-${type}`];
        }

        return ret;
    }

    const { mutate: errorLogSubmission } = useMutation({
        mutationFn: (values) => {
            return reportAPI.errorLog(values)
        }
    });

    const submitForm = async (values, actions) => {

        try {
            // 1. Switch to native FormData to handle multipart boundaries correctly
            const apiPayload = new FormData();
            const ReactSwal = withReactContent(Swal);

            // 2. Build the initial plain objects expected by the /rectify backend
            let metadataObj = {
                inspection_record_id: inspection_record?.ascendant_tree?.[0]?.record?._id || inspection_record._id,
                assignment_record_id: inspection_record._id,
                organisation_id: inspection_record.organisation_id,
            };

            let toUpdateObj = {};

            let contentsObj = {
                title: values.title == "" ? values.inspection_type.trim() : values.title.trim(),
                description: values.description.trim(),
                maintenance_tag: values.maintenance_tag == "" ? undefined : values.maintenance_tag,
                recordedAt: values.recordedAt == "" ? undefined : new Date(values.recordedAt).toISOString().replace('Z', '+00:00'),
                priority: values.priority != "" ? values.priority : "low",
                technician: selected_user.user.nickname,
                devices: [],
            };

            const deviceLabels = ['devicePrimary', 'deviceSecondary'];
            const flatImagesQueue = [];

            // 3. Build initial metadata and flatten images into a single queue for the async limiter
            deviceLabels.forEach((deviceLabel, deviceIndex) => {
                if (values[`${deviceLabel}-info`] != "") {
                    contentsObj.devices[deviceIndex] = {
                        uuid: values[`${deviceLabel}-uuid`],
                        name: values[`${deviceLabel}-info`].label,
                        serial_no: values[`${deviceLabel}-serial_no`],
                        stop_id: values[`${deviceLabel}-stop_id`],
                        conditions: getObjValues(values, `${deviceLabel}-conditions`),
                        actions: getObjValues(values, `${deviceLabel}-actions`),
                        images: [] // Will be populated after compression
                    };

                    const imagesArray = values[`${deviceLabel}-images`] || [];
                    imagesArray.forEach((image, imageIndex) => {
                        flatImagesQueue.push({
                            deviceIndex,
                            key: `device${deviceIndex}_image${imageIndex}`,
                            file: image.file
                        });
                    });
                }
            });

            const totalImages = flatImagesQueue.length;
            let compressedCount = 0;

            ReactSwal.fire({
                icon: "info",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:24px">Preparing Submission</div>`,
                html: renderContent(),
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    updateProgressUI('compressing', 0, totalImages);
                },
                width: deviceLayout == "mobile" ? '20rem' : '32rem'
            });

            // 4. Process EXACTLY 3 images at a time to protect RAM
            const compressionOptions = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };

            const compressedResults = await async.mapLimit(flatImagesQueue, 3, async (item) => {
                const compressedBlob = await imageCompression(item.file, compressionOptions);
                const compressedFile = new File([compressedBlob], item.file.file?.name || item.file.name, { type: compressedBlob.type });

                compressedCount++;
                updateProgressUI('compressing', compressedCount, totalImages);

                return {
                    deviceIndex: item.deviceIndex,
                    key: item.key,
                    fileName: compressedFile.name,
                    compressedFile: compressedFile
                };
            });

            // 5. Reassemble the FormData payload
            compressedResults.forEach((result) => {
                apiPayload.append(result.key, result.compressedFile);
                contentsObj.devices[result.deviceIndex].images.push({
                    key: result.key,
                    fileName: result.fileName
                });
            });

            toUpdateObj.status = determineInspectionStatus("rectification", contentsObj);

            // Append the stringified objects to the FormData so multer can read them as text fields
            apiPayload.append('to_update', JSON.stringify(toUpdateObj));
            apiPayload.append('metadata', JSON.stringify(metadataObj));
            apiPayload.append('contents', JSON.stringify(contentsObj));

            // 6. Transition to Upload Phase
            const titleEl = Swal.getTitle();
            if (titleEl) titleEl.innerHTML = `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:24px">Uploading...</div>`;

            const handleUploadProgress = (percent) => {
                updateProgressUI('uploading', totalImages, totalImages, percent);
            };

            // Fire the Axios submission
            await onSubmit([apiPayload, handleUploadProgress]);

            await Swal.fire({
                icon: "success",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saved!</div>',
                showConfirmButton: false,
                allowOutsideClick: false,
                timer: 2000,
                width: deviceLayout == "mobile" ? '20rem' : '32rem'
            });

            navigate(-1);

        } catch (err) {
            console.error("Submission failed:", err);
            actions.setSubmitting(false);

            errorLogSubmission({
                category: 'assignment',
                status: err.status || 500,
                data: JSON.stringify(err),
            });

            await Swal.fire({
                icon: "error",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error!</div>',
                html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${err.message || "Something went wrong while saving."}</div>`,
                showConfirmButton: true,
                timer: 15000,
                width: deviceLayout == "mobile" ? '20rem' : '32rem'
            });
        }
    }

    const formInitialValues = useMemo(() => {
        if (!inspection_record || !inspection_record.devices || !inspection_record.devices[0]) {
            return defaultServiceValues;
        }

        const primary = inspection_record.devices[0];
        let initial = { ...defaultServiceValues };

        // Map Basic Info
        initial['devicePrimary-uuid'] = primary.uuid || "";
        initial['devicePrimary-serial_no'] = primary.serial_no || "";
        initial['devicePrimary-stop_id'] = primary.stop_id || "";
        initial['devicePrimary-info'] = { label: primary.name };

        // Map Conditions
        if (primary.conditions) {
            Object.keys(primary.conditions).forEach(key => {
                initial[`devicePrimary-conditions-${key}`] = primary.conditions[key];
            });
        }

        // Map Actions
        if (primary.actions) {
            Object.keys(primary.actions).forEach(key => {
                initial[`devicePrimary-actions-${key}`] = primary.actions[key];
            });
        }

        return initial;
    }, [inspection_record]);

    return (
        <SoftBox height={deviceLayout == "mobile" ? "calc(100% - 16px)" : "calc(100% - 2rem)"} px={1} pb={1} py={deviceLayout == "mobile" ? 1 : "1rem"}>
            {deviceLayout != "mobile" && (
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}
            <Formik
                initialValues={formInitialValues}
                validationSchema={currentValidation}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={handleSubmit}>
                {({ values, errors, touched, isSubmitting, setFieldValue, handleChange }) => {
                    Object.keys(errors).length > 0 && console.log("Validation Errors:", errors);

                    return (
                        <Form id={formId} autoComplete="off" style={{ height: deviceLayout == "mobile" ? '100%' : 'unset' }}>
                            <Card sx={{ overflow: "visible", height: '100%' }}>
                                <SoftBox height="100%" display="flex" flexDirection="column" justifyContent="space-between">
                                    {getStepContent(activeStep, {
                                        values,
                                        touched,
                                        errors,
                                        isSubmitting,
                                        formField,
                                        functions: {
                                            setDeviceOptions,
                                            setFetchedUuids,
                                            setFieldValue,
                                            handleChange,
                                            handleBack,
                                            handleCancel
                                        },
                                        custom: {
                                            fetchedUuids,
                                            fetchDevices,
                                            activeStep,
                                            isLastStep,
                                            user: selected_user,
                                            inspection_record,
                                            all_devices,
                                            deviceOptions,
                                            maintenanceOptions
                                        }
                                    },
                                        deviceLayout,
                                        userPermissions
                                    )}
                                </SoftBox>
                            </Card>
                        </Form>
                    )
                }}
            </Formik>
        </SoftBox>
    );
}

AssignmentForm.propTypes = {
    selected_user: PropTypes.object.isRequired,
    all_devices: PropTypes.array.isRequired,
    inspection_record: PropTypes.object,
    onSubmit: PropTypes.func,
};

export default AssignmentForm;